import React, { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { FaClock, FaCalendar, FaSave, FaCog, FaInfoCircle } from 'react-icons/fa';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const { settings, updateSettings, getNextOrderDeadline, getNextDeliveryDate } = useSettings();
  const [formData, setFormData] = useState({
    orderCutoffTime: settings.orderCutoffTime,
    orderCutoffDays: settings.orderCutoffDays,
    deliveryDays: settings.deliveryDays,
    isOrderingOpen: settings.isOrderingOpen
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      updateSettings(formData);
      toast.success('Settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const nextDeadline = getNextOrderDeadline();
  const nextDelivery = getNextDeliveryDate();

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <FaCog className="me-2" />
          Admin Settings
        </h1>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card">
            <div className="card-body">
              <h3 className="card-title mb-4">Order Management Settings</h3>
              
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="orderCutoffTime">
                        <FaClock className="me-2" />
                        Daily Order Cut-off Time
                      </label>
                      <input
                        type="time"
                        id="orderCutoffTime"
                        name="orderCutoffTime"
                        className="form-control"
                        value={formData.orderCutoffTime}
                        onChange={handleInputChange}
                        required
                      />
                      <small className="form-text text-secondary">
                        Orders will not be accepted after this time each day
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="orderCutoffDays">
                        <FaCalendar className="me-2" />
                        Days Before Delivery to Close Orders
                      </label>
                      <input
                        type="number"
                        id="orderCutoffDays"
                        name="orderCutoffDays"
                        className="form-control"
                        value={formData.orderCutoffDays}
                        onChange={handleInputChange}
                        min="0"
                        max="7"
                        required
                      />
                      <small className="form-text text-secondary">
                        Orders close this many days before delivery
                      </small>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="deliveryDays">
                        <FaCalendar className="me-2" />
                        Days After Order for Delivery
                      </label>
                      <input
                        type="number"
                        id="deliveryDays"
                        name="deliveryDays"
                        className="form-control"
                        value={formData.deliveryDays}
                        onChange={handleInputChange}
                        min="1"
                        max="7"
                        required
                      />
                      <small className="form-text text-secondary">
                        How many days after order deadline to deliver
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="isOrderingOpen"
                          name="isOrderingOpen"
                          className="form-check-input"
                          checked={formData.isOrderingOpen}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="isOrderingOpen">
                          Enable Ordering
                        </label>
                      </div>
                      <small className="form-text text-secondary">
                        Toggle to completely disable ordering system
                      </small>
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-4">
                <FaInfoCircle className="me-2" />
                Current Schedule
              </h4>
              
              <div className="mb-4">
                <h5>Next Order Deadline</h5>
                <div className="p-3 bg-light rounded">
                  <div className="d-flex align-items-center mb-2">
                    <FaClock className="text-primary me-2" />
                    <strong>{nextDeadline.toLocaleDateString()}</strong>
                  </div>
                  <p className="mb-0 text-secondary">
                    {nextDeadline.toLocaleTimeString()} ({settings.orderCutoffTime})
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <h5>Next Delivery Date</h5>
                <div className="p-3 bg-light rounded">
                  <div className="d-flex align-items-center mb-2">
                    <FaCalendar className="text-success me-2" />
                    <strong>{nextDelivery.toLocaleDateString()}</strong>
                  </div>
                  <p className="mb-0 text-secondary">
                    {nextDelivery.toLocaleDateString('en-US', { weekday: 'long' })}
                  </p>
                </div>
              </div>

              <div className="alert alert-info">
                <h6>How it works:</h6>
                <ul className="mb-0 small">
                  <li>Orders close daily at {settings.orderCutoffTime}</li>
                  <li>Delivery happens {settings.deliveryDays} days after order deadline</li>
                  <li>Users see real-time countdown to deadline</li>
                  <li>Settings apply immediately after saving</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings; 